import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedCounter from '../common/AnimatedCounter';
import { Users, BookOpen, Award, Clock, TrendingUp, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const StatsSection = ({ company }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const stats = [
    {
      icon: Users,
      value: company?.stats?.studentsTrained || 5000,
      label: 'Students Trained',
      suffix: '+',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      darkBgColor: 'dark:bg-blue-900'
    },
    {
      icon: BookOpen,
      value: company?.stats?.coursesOffered || 50,
      label: 'Courses Offered',
      suffix: '+',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      darkBgColor: 'dark:bg-green-900'
    },
    {
      icon: Award,
      value: company?.stats?.successRate || 98,
      label: 'Success Rate',
      suffix: '%',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      darkBgColor: 'dark:bg-purple-900'
    },
    {
      icon: Clock,
      value: company?.stats?.yearsExperience || 15,
      label: 'Years Experience',
      suffix: '+',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      darkBgColor: 'dark:bg-orange-900'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  return (
    <section ref={ref} className="section-padding bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-hero-pattern opacity-10"></div>
      
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/5 rounded-full blur-2xl"></div>
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Trusted by Engineers Worldwide
          </motion.h2>
          <motion.p 
            variants={itemVariants}
            className="text-xl text-blue-100 max-w-3xl mx-auto"
          >
            Our numbers speak for themselves. Join thousands of successful engineers who have transformed their careers with Prime Edge.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-8"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
                className="text-center group"
              >
                <div className="relative">
                  {/* Icon Background */}
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className={`w-20 h-20 ${stat.bgColor} ${stat.darkBgColor} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg transition-all duration-300`}
                  >
                    <Icon className={`w-10 h-10 ${stat.color}`} />
                  </motion.div>

                  {/* Floating Animation */}
                  <motion.div
                    animate={{ 
                      y: [0, -5, 0],
                      rotate: [0, 2, 0, -2, 0]
                    }}
                    transition={{ 
                      duration: 4,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                    className="absolute inset-0 w-20 h-20 mx-auto"
                  >
                    <div className={`w-full h-full ${stat.bgColor} ${stat.darkBgColor} rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
                  </motion.div>
                </div>

                {/* Counter */}
                <motion.div
                  variants={itemVariants}
                  className="mb-2"
                >
                  <AnimatedCounter
                    end={stat.value}
                    suffix={stat.suffix}
                    className={`text-3xl md:text-4xl font-bold ${stat.color} group-hover:scale-110 transition-transform duration-300`}
                    duration={2}
                  />
                </motion.div>

                {/* Label */}
                <motion.p
                  variants={itemVariants}
                  className="text-blue-100 font-medium text-sm md:text-base group-hover:text-white transition-colors duration-300"
                >
                  {stat.label}
                </motion.p>

                {/* Progress Bar */}
                <motion.div
                  initial={{ width: 0 }}
                  animate={inView ? { width: '100%' } : { width: 0 }}
                  transition={{ duration: 1.5, delay: index * 0.2 }}
                  className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden"
                >
                  <motion.div
                    initial={{ x: '-100%' }}
                    animate={inView ? { x: '0%' } : { x: '-100%' }}
                    transition={{ duration: 1.5, delay: index * 0.2 + 0.5 }}
                    className="h-full bg-white rounded-full"
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <Link to={"/courses"} className="px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-lg hover:shadow-xl">
              Join Our Success Stories
            </Link >
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default StatsSection;

